// src/utils/quizUtils.js

/**
 * Shuffles an array in place using the Fisher-Yates algorithm.
 * @param {Array} array 
 * @returns {Array} The shuffled array
 */
export const shuffleArray = (array) => {
  let currentIndex = array.length, randomIndex;
  while (currentIndex !== 0) {
    randomIndex = Math.floor(Math.random() * currentIndex);
    currentIndex--;
    [array[currentIndex], array[randomIndex]] = [array[randomIndex], array[currentIndex]];
  }
  return array;
};

// --- EXAM HISTORY (LOCAL STORAGE) ---
const EXAM_HISTORY_KEY = 'examHistory';

/**
 * Retrieves the list of completed exams from local storage.
 * @returns {Array} An array of exam result objects.
 */
export const getExamHistory = () => {
    try {
        const history = localStorage.getItem(EXAM_HISTORY_KEY);
        const parsed = history ? JSON.parse(history) : [];
        // Ensure we return an array. If legacy data existed (user-specific object structure), fallback to empty.
        return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
        console.error("Could not retrieve exam history", error);
        return [];
    }
};

/**
 * Saves a completed exam result to local storage.
 * @param {Object} examResult - The result object containing score, answers, date, etc.
 */
export const saveExamResult = (examResult) => {
    const history = getExamHistory();
    history.push(examResult);
    
    try {
        localStorage.setItem(EXAM_HISTORY_KEY, JSON.stringify(history));
    } catch (error) {
        console.error("Could not save exam to history", error);
    }
};

// --- IN-PROGRESS QUIZ STATE PERSISTENCE ---
const IN_PROGRESS_QUIZ_KEY = 'inProgressQuiz';

/**
 * Saves the entire state of the current quiz to localStorage.
 * Useful for page refreshes or accidental closures.
 * @param {Object} state - The current quiz state object.
 */
export const saveQuizState = (state) => {
    try {
        // We also add a timestamp to know how old the saved state is
        const stateToSave = { ...state, savedAt: Date.now() };
        localStorage.setItem(IN_PROGRESS_QUIZ_KEY, JSON.stringify(stateToSave));
    } catch (error) {
        console.error("Could not save quiz state", error);
    }
};

/**
 * Loads the saved quiz state from localStorage.
 * @returns {Object|null} The saved state or null if not found/error.
 */
export const loadQuizState = () => {
    try {
        const savedState = localStorage.getItem(IN_PROGRESS_QUIZ_KEY);
        if (!savedState) return null;

        const parsedState = JSON.parse(savedState);
        return parsedState;
    } catch (error) {
        console.error("Could not load quiz state", error);
        return null;
    }
};

/**
 * Clears the saved quiz state.
 * Called when a quiz is finished or abandoned.
 */
export const clearQuizState = () => {
    localStorage.removeItem(IN_PROGRESS_QUIZ_KEY);
};
